//Nov5 DraggableItem.js:
// === DraggableItem.jsx (May 1 – full debug instrumentation) ===
import React, { useRef, useEffect, useMemo } from 'react';
import { useDrag } from 'react-dnd';
import { getEmptyImage } from 'react-dnd-html5-backend';
import FieldRenderer from '../../Fields/FieldRenderer';
import { useFormLayout } from '../../../contexts/FormLayoutContext';
import { useFieldMetadata } from '../../../contexts/FieldMetadataContext';
import { DND_DEBUG } from './constants';

const DraggableItem = ({
  id, label, type, required, index, rowIndex,
}) => {
  const {
    layoutConfig,            // still injected, not logged for brevity
    handleDrop,              // not used here but kept
    showDropZones,
    setShowDropZones,
    handleDragStart,
    handleDragEnd,
  } = useFormLayout();

  const { fieldsMetadata, isLoading } = useFieldMetadata();

  const fieldMetadata = useMemo(() => fieldsMetadata[id] || {}, [fieldsMetadata, id]);
  const layoutStyle   = useMemo(() => fieldMetadata.style || {}, [fieldMetadata.style]);

  const isSubmitButton = useMemo(() => type === 'submit', [type]);
  const isIconField    = useMemo(() =>
    fieldMetadata?.allowedImageTypes?.includes('icon'), [fieldMetadata]);
  const isBannerField  = useMemo(() =>
    fieldMetadata?.allowedImageTypes?.includes('banner'), [fieldMetadata]);
  const isHeaderField  = useMemo(() => (fieldMetadata?.type || type) === 'header', [fieldMetadata, type]);



  useEffect(() => {
    if (!fieldsMetadata[id] || !fieldsMetadata[id].type) {
      console.error(`[DraggableItem] Missing metadata for item ${id}`, fieldsMetadata[id]);
    }
  }, [fieldsMetadata, id]);

  const ref = useRef(null);
  const hasMountedRef = useRef(false); // prevents initial mount from calling handleDragEnd

  /* ---------------------------------------------------------------------- */
  /*                         Pointer-level instrumentation                  */
  /* ---------------------------------------------------------------------- */
  useEffect(() => {
    if (!ref.current) return;

    const handleDown = (e) => {
      // console.log('[DraggableItem] mousedown on', id, {
      //   x: e.clientX, y: e.clientY, target: e.target,
      // });
    };
    const handleMove = (e) => {
      // console.log('[DraggableItem] mousemove on', id, {
      //   x: e.clientX, y: e.clientY,
      // });
    };
    const handleUp = () => {
      // console.log('[DraggableItem] mouseup on', id);
    };
    
    const node = ref.current;
    node.addEventListener('mousedown', handleDown);
    node.addEventListener('mousemove', handleMove);
    node.addEventListener('mouseup',   handleUp);

    return () => {
      node.removeEventListener('mousedown', handleDown);
      node.removeEventListener('mousemove', handleMove);
      node.removeEventListener('mouseup',   handleUp);
    };
  }, [ref, id]);

  /* ---------------------------------------------------------------------- */
  /*                            React-DnD setup                             */
  /* ---------------------------------------------------------------------- */
  const [{ isDragging }, drag, preview] = useDrag(
    () => ({
      type: isSubmitButton ? 'none' : 'field',

      /* ---- beginDrag ---------------------------------------------------- */
      item: () => {
      const rect = ref.current?.getBoundingClientRect?.() || null;
      const dragItem = {
        id,
        index,
        rowIndex,
        type,
        sourceRect: rect ? { width: rect.width, height: rect.height } : null,
      };

        if (DND_DEBUG) {
          console.log('[DnD] beginDrag', {
            id,
            rowIndex,
            index,
            type,
          });
        }

        // Drag lifecycle should be the single source of truth for drop zone UI.
        //handleDragStart?.(id);
        // NOTE: do NOT arm drop zones here. We arm based on monitor.isDragging() below.

        return dragItem;
      },


      /* ---- canDrag ------------------------------------------------------ */
      canDrag: (monitor) => {
        const allowed = !isSubmitButton;
        if (!allowed) {
//          console.log(`[DraggableItem] canDrag FALSE for ${id}`);           /*  DEBUG */
        }
        return allowed;
      },

      /* ---- collect ------------------------------------------------------ */
      collect: (monitor) => {
        const dragging = monitor.isDragging();
        if (dragging) {
          const start = monitor.getInitialClientOffset();
//          console.log(`[DraggableItem] isDragging TRUE for ${id}`, start);  /*  DEBUG */
        }
        return { isDragging: !!dragging };
      },

      /* ---- end ---------------------------------------------------------- */
end: (item, monitor) => {
  const dropped = monitor.didDrop();

  if (DND_DEBUG) {
    console.log('[DnD] endDrag', {
      id,
      dropped,
      finalClientOffset: monitor.getClientOffset?.() || null,
    });
  }

  console.log(`[DraggableItem] end drag ${id}, dropped=${dropped}`);  /*  DEBUG */
  // NOTE: Do NOT call handleDragEnd here. Dropzone arming/disarming is driven
  // by monitor.isDragging() via the effect below to prevent flicker/thrashing.
},

    }),
    [
      id,
      index,
      rowIndex,
      type,
      isSubmitButton,
    ],
  );

  /* Attach drag + preview once refs resolve */
useEffect(() => {
  if (!ref.current) return;
  preview(getEmptyImage(), { captureDraggingState: true });
  drag(ref);
//    console.log(`[DraggableItem] drag & preview wired for ${id}`);          /*  DEBUG */
}, [preview, drag]);

  /* ---------------------------------------------------------------------- */
  /*             Dynamic width / spacing (unchanged from before)            */
  /* ---------------------------------------------------------------------- */
  const dynamicStyle = useMemo(() => {
    let width = '100%';
    if (isIconField)   width = 'fit-content';
    if (isBannerField) width = '100%';

    const spacingStyle = {
      marginLeft:  isIconField ? '16px' : layoutStyle.marginLeft  || '0px',
      marginRight: isIconField ? '16px' : layoutStyle.marginRight || '0px',
    };

    return {
      display: 'flex',
      width,
      minWidth: 0,
      // For headers, let content define height; don’t stretch
      alignItems: isHeaderField ? 'flex-start' : 'stretch',
      height:    isHeaderField ? 'auto'       : '100%',

      ...spacingStyle,
      paddingTop:    layoutStyle.paddingTop    || '0px',
      paddingBottom: layoutStyle.paddingBottom || '0px',
    };
  }, [layoutStyle, isIconField, isBannerField, isHeaderField]);

  /* Guard until metadata ready */
  if (isLoading || !fieldMetadata || Object.keys(fieldMetadata).length === 0) {
//    console.log(`[DraggableItem] Waiting for metadata for ${id}`);          /*  DEBUG */
    return null;
  }

  /* ---------------------------------------------------------------------- */
  /*        Hide drop-zones for *this* item while it’s being dragged        */
  /* ---------------------------------------------------------------------- */
  const effectiveShowDropZones = showDropZones && !isDragging;

  return (
    <div style={dynamicStyle}>
      <div
        ref={ref}
        style={{
          flexGrow: isIconField ? 0 : 1,
          display: 'flex',
          // For headers, avoid vertical stretch so the row height wraps the text
          alignItems: isHeaderField ? 'flex-start' : 'stretch',
          height:    isHeaderField ? 'auto'       : '100%',
          width: '100%',
          minWidth: 0,

        }}
      >
        <FieldRenderer
          id={id}
          type={fieldMetadata.type || type}
          label={fieldMetadata.label || label}
          required={fieldMetadata.required || required}
          index={index}
          mode="builder"
          fieldMetadata={fieldMetadata}
          showDropZones={effectiveShowDropZones}
          setShowDropZones={setShowDropZones}
          isDragging={isDragging}
        />
      </div>
    </div>
  );
};

export default DraggableItem;
